// climateNormals.js — seasonal climate normals from the Open-Meteo archive.
//
// Google Maps has no climate data, so — like the elevation lookup — this uses
// Open-Meteo's keyless, CORS-enabled Historical Weather API (ERA5 reanalysis).
// For each site it pulls ~10 years of daily highs, lows, and precipitation and
// reduces them to hemisphere-aware seasonal normals:
//   - "day"   temperature  = average daily maximum in the season
//   - "night" temperature  = average daily minimum in the season
//   - precipitation (mm)   = average total precipitation per season
// Seasons follow the local hemisphere, so "winter" is always the cold season.
//
// fetchClimateNormals([{ id, lat, lng }]) →
//   Promise<{ normals: Map<id, normals>, failures: [{ id, reason }] }>, where
// normals = { winterDayC, winterNightC, summerDayC, summerNightC,
//             winterPrecipMm, summerPrecipMm, source, period, updatedAt,
//             gridLat, gridLng }. Sites that error or return no usable data are
// reported in `failures` (with a reason) rather than silently dropped, so the
// caller can tell rate-limiting (retry) apart from a genuine no-data cell.

const ARCHIVE = 'https://archive-api.open-meteo.com/v1/archive';

const avg = (arr) => (arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : null);
const round1 = (x) => (x == null ? null : Math.round(x * 10) / 10);
const round0 = (x) => (x == null ? null : Math.round(x));
const round2 = (x) => (Number.isFinite(x) ? Math.round(x * 100) / 100 : null);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// A normals object is only worth keeping if at least one field resolved; an
// all-null result (e.g. a grid cell with no coverage) must NOT be persisted, or
// the project stops being a fetch candidate and gets stuck blank forever.
const hasAnyNormal = (n) =>
  n && (n.winterDayC != null || n.winterNightC != null || n.summerDayC != null
    || n.summerNightC != null || n.winterPrecipMm != null || n.summerPrecipMm != null);

const backoffMs = (attempt) => Math.min(16000, 800 * 2 ** attempt) + Math.floor(Math.random() * 300);

// The archive (ERA5) endpoint is heavily weighted — ~10 years × 3 daily
// variables per request — so a bulk enrichment easily trips Open-Meteo's
// per-minute rate limit, which surfaces as HTTP 429 (and occasionally 5xx).
// Retry those transparently with exponential backoff, honouring Retry-After,
// so a single click fills in every site instead of silently dropping most.
async function fetchArchiveJson(url, retries = 4) {
  for (let attempt = 0; ; attempt++) {
    let res;
    try {
      // eslint-disable-next-line no-await-in-loop
      res = await fetch(url);
    } catch (e) {
      if (attempt >= retries) throw new Error('network error');
      // eslint-disable-next-line no-await-in-loop
      await sleep(backoffMs(attempt));
      continue;
    }
    if (res.status === 429 || res.status >= 500) {
      if (attempt >= retries) throw new Error(`rate-limited (HTTP ${res.status})`);
      const ra = Number(res.headers.get('retry-after'));
      const wait = Number.isFinite(ra) && ra > 0 ? ra * 1000 : backoffMs(attempt);
      // eslint-disable-next-line no-await-in-loop
      await sleep(wait);
      continue;
    }
    let data;
    try {
      // eslint-disable-next-line no-await-in-loop
      data = await res.json();
    } catch {
      throw new Error(`bad response (HTTP ${res.status})`);
    }
    // Open-Meteo reports parameter errors as { error: true, reason }.
    if (data && data.error) throw new Error(String(data.reason || 'API error'));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return data;
  }
}

// Reduce a daily series to seasonal normals. Northern hemisphere: winter =
// Dec–Feb, summer = Jun–Aug; southern hemisphere: swapped. Exported for testing.
export function computeNormals(daily, lat) {
  const north = lat >= 0;
  const winterMonths = north ? [12, 1, 2] : [6, 7, 8];
  const summerMonths = north ? [6, 7, 8] : [12, 1, 2];
  const time = daily.time || [];
  const tmax = daily.temperature_2m_max || [];
  const tmin = daily.temperature_2m_min || [];
  const prcp = daily.precipitation_sum || [];

  const wDay = [], wNight = [], sDay = [], sNight = [];
  let wPrcp = 0, sPrcp = 0;
  const winterYears = new Set(), summerYears = new Set();

  for (let i = 0; i < time.length; i++) {
    const t = time[i];
    if (!t) continue;
    const month = Number(t.slice(5, 7));
    const year = t.slice(0, 4);
    if (winterMonths.includes(month)) {
      if (Number.isFinite(tmax[i])) wDay.push(tmax[i]);
      if (Number.isFinite(tmin[i])) wNight.push(tmin[i]);
      if (Number.isFinite(prcp[i])) wPrcp += prcp[i];
      winterYears.add(year);
    } else if (summerMonths.includes(month)) {
      if (Number.isFinite(tmax[i])) sDay.push(tmax[i]);
      if (Number.isFinite(tmin[i])) sNight.push(tmin[i]);
      if (Number.isFinite(prcp[i])) sPrcp += prcp[i];
      summerYears.add(year);
    }
  }

  return {
    winterDayC: round1(avg(wDay)),
    winterNightC: round1(avg(wNight)),
    summerDayC: round1(avg(sDay)),
    summerNightC: round1(avg(sNight)),
    winterPrecipMm: winterYears.size ? round0(wPrcp / winterYears.size) : null,
    summerPrecipMm: summerYears.size ? round0(sPrcp / summerYears.size) : null,
  };
}

// Returns { normals: Map<id, normals>, failures: [{ id, reason }] }. Every
// coordinate is a building site, so we ask Open-Meteo for the nearest *land*
// grid cell (cell_selection=land) using its 90 m DEM — that both keeps coastal /
// island / lakeside sites off empty sea cells and picks a cell of similar
// elevation for mountain sites, which is as close as a gridded reanalysis gets
// to "the nearest weather station". Concurrency is deliberately low and paired
// with backoff so a bulk run stays under the archive endpoint's rate limit.
export async function fetchClimateNormals(points, { years = 10, concurrency = 2 } = {}) {
  const valid = (points || []).filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  const out = new Map();
  const failures = [];
  if (!valid.length) return { normals: out, failures };

  const lastFullYear = new Date().getFullYear() - 1;
  const firstYear = lastFullYear - years + 1;
  const startDate = `${firstYear}-01-01`;
  const endDate = `${lastFullYear}-12-31`;
  const period = `${firstYear}-${lastFullYear}`;
  const updatedAt = new Date().toISOString().slice(0, 10);

  const queue = [...valid];
  const worker = async () => {
    while (queue.length) {
      const p = queue.shift();
      const url = `${ARCHIVE}?latitude=${p.lat}&longitude=${p.lng}`
        + `&start_date=${startDate}&end_date=${endDate}`
        + '&daily=temperature_2m_max,temperature_2m_min,precipitation_sum'
        + '&timezone=auto&cell_selection=land';
      try {
        // eslint-disable-next-line no-await-in-loop
        const data = await fetchArchiveJson(url);
        const daily = data && data.daily;
        if (!daily || !Array.isArray(daily.time) || !daily.time.length) {
          failures.push({ id: p.id, reason: 'empty response' });
          continue;
        }
        const n = computeNormals(daily, p.lat);
        if (!hasAnyNormal(n)) {
          failures.push({ id: p.id, reason: 'no data for nearest land cell' });
          continue;
        }
        out.set(p.id, {
          ...n, source: 'open-meteo-era5', period, updatedAt,
          // The grid cell Open-Meteo actually resolved to (its "nearest station").
          gridLat: round2(data.latitude), gridLng: round2(data.longitude),
        });
      } catch (e) {
        failures.push({ id: p.id, reason: e?.message || 'request failed' });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, valid.length) }, worker));
  return { normals: out, failures };
}

// Compact display helpers, shared by the overview table and the Comms facts.
export function fmtSeasonTemps(n) {
  if (!n) return null;
  const d = n.day != null ? `${Math.round(n.day)}°` : '–';
  const nt = n.night != null ? `${Math.round(n.night)}°` : '–';
  if (n.day == null && n.night == null) return null;
  return `${d}/${nt}`;
}
